import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["amount"]
  static values = { lastCents: { type: Number, default: 0 } }

  connect() {
    // Store initial balance for countUp delta
    if (this.hasAmountTarget) {
      this.lastCentsValue = this._parseCents(this.amountTarget.textContent)
    }

    this.subscription = consumer.subscriptions.create(
      { channel: "UserNotificationsChannel" },
      {
        received: (data) => this.handleNotification(data)
      }
    )
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  // When Turbo Stream replaces the target, animate from last known value
  amountTargetConnected() {
    const newCents = this._parseCents(this.amountTarget.textContent)

    if (this.lastCentsValue > 0 && this.lastCentsValue !== newCents) {
      this._animateBalance(this.lastCentsValue, newCents)
    }
    this.lastCentsValue = newCents
  }

  handleNotification(data) {
    if (data.type === "balance_updated" && data.balance_cents != null && this.hasAmountTarget) {
      const from = this.lastCentsValue || this._parseCents(this.amountTarget.textContent)
      const to = data.balance_cents
      this.lastCentsValue = to
      this._animateBalance(from, to)
    }
  }

  _animateBalance(from, to) {
    if (from === to) return

    const el = this.amountTarget

    if (this.prefersReducedMotion) {
      el.textContent = this.formatCop(to)
      return
    }

    // Cancel any running animation
    if (this._animFrame) cancelAnimationFrame(this._animFrame)

    const duration = 400
    const start = performance.now()

    const step = (now) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      // ease-out cubic: 1 - (1-t)^3
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = Math.round(from + (to - from) * eased)
      el.textContent = this.formatCop(current)

      if (progress < 1) {
        this._animFrame = requestAnimationFrame(step)
      } else {
        this._animFrame = null
      }
    }

    this._animFrame = requestAnimationFrame(step)
  }

  _parseCents(text) {
    const digits = text.replace(/[^\d]/g, "")
    return parseInt(digits) * 100 || 0
  }

  formatCop(cents) {
    const cop = cents / 100
    return `$${cop.toLocaleString("es-CO")}`
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
